// The original kissaderby.fi/modern sound clips and their tables, copied from its app.js
// (.taiga/knowledge/landscape/kissaderby-modern-app.js; cue map in kissaderby-modern-sound.md).
// The files are in public/assets/kissaderby/modern/audio/ under their original names.
import type { CatId } from '../race/types';

/** A commentator line: [seconds the busy gate holds for it, file]. */
export type Line = readonly [number, string];

/** URL of a clip. Names keep their spaces, commas and apostrophes, so they are encoded as the original does. */
export function clipUrl(file: string): string {
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  return `${base}assets/kissaderby/modern/audio/${encodeURI(file)}`;
}

/** The announcer saying each cat's name. */
export const NAME_CLIP: Record<CatId, string> = {
  nemo: 'nemo.mp3', sirius: 'sirius.mp3', tyyne: 'tyyne.mp3', silkki: 'silkkihieno.mp3', misse: 'misse.mp3', growl: 'growltiger.mp3',
};

/** Seconds, from the original table; holds the busy gate while a call is spoken. */
export const CLIP_LEN: Record<string, number> = {
  'nemo.mp3': 0.6, 'sirius.mp3': 0.6, 'tyyne.mp3': 0.8, 'silkkihieno.mp3': 1.0, 'misse.mp3': 0.6, 'growltiger.mp3': 0.9,
  'foundham.mp3': 2, 'atecatnip.mp3': 3, 'hamsprint.mp3': 1.5, '1stplaceovertake1.mp3': 1.2, '1stplaceovertake2.mp3': 1.0,
  'winner.mp3': 1.3, '2ndplace.mp3': 1.8, '4thplace.mp3': 1.5, '5thplace.mp3': 1.4, 'lastplace.mp3': 1.2, 'lastplacespecial.mp3': 5.9,
  'dopingtest.mp3': 4.2, 'posttime.mp3': 5.9, 'catmetal.mp3': 26.4, 'trip.mp3': 10.2, 'purring.mp3': 1.6, 'meow0.mp3': 2.2, 'meow1.mp3': 1.2,
};

/** The commentator's pools: joke lead-ins, jokes, lines during a race and lines between races. */
export const LINES: Record<'prejoke' | 'joke' | 'running' | 'notrunning', readonly Line[]> = {
  prejoke: [[4, 'joke, jest.mp3'], [5, 'joke, joke.mp3'], [5, 'joke, laugh.mp3']],
  joke: [[7, 'joke1, hiss.mp3'], [7, 'joke10, meowntain.mp3'], [8, 'joke12, can u hear meow.mp3'], [8, 'joke6, mice krispies.mp3'],
    [9, 'joke11, cat-alouge.mp3'], [9, 'joke14, furraris.mp3'], [9, 'joke2, cheetahs.mp3'], [9, 'joke3, claw enforcement.mp3'],
    [9, 'joke4, mittens.mp3'], [9, 'joke7, bad mewd.mp3'], [9, 'joke8, bark.mp3'], [10, 'joke5, purrputrator.mp3'],
    [10, 'joke9, cat-has-trophy.mp3'], [11, 'betting time over.mp3'], [14, 'joke13, copycats.mp3']],
  running: [[6, 'cat position system2.mp3'], [6, 'cat position system3.mp3'], [6, 'live broadcast.mp3'], [7, 'bets are off.mp3'],
    [8, 'cat position system1.mp3'], [8, 'cat position system4.mp3'], [8, 'excited.mp3'], [8, 'host, trusted.mp3'],
    [8, "i don't remember2.mp3"], [8, 'top of the top.mp3'], [9, "i don't remember1.mp3"], [9, "i don't remember3.mp3"],
    [10, 'host, killer.mp3'], [11, 'cat position system, proud.mp3'], [11, 'fell off chair.mp3'], [11, 'pata a pata.mp3'],
    [11, 'sold out, ticket.mp3'], [11, 'weather is beautiful.mp3'], [12, 'microphone is on.mp3'], [12, 'top cathletes.mp3'],
    [14, 'cats in town.mp3'], [17, 'catnip, doping.mp3'], [17, 'raining cats and dogs.mp3']],
  notrunning: [[6, 'live at cat derby.mp3'], [7, 'end, good day.mp3'], [8, '4.20PM about to start.mp3'], [8, 'April 20th any given moment.mp3'],
    [8, 'hyped.mp3'], [9, '4.20PM competition.mp3'], [9, 'ends, new race beginning.mp3'], [9, 'pinnacle.mp3'], [10, 'host, amazing.mp3'],
    [11, 'thank you, see you soon.mp3'], [18, 'rat on the field.mp3'], [22, 'dog in audience.mp3']],
};

/** The first line after sound is turned on. */
export const MIC_ON: Line = [12, 'microphone is on.mp3'];

/** Long clips reported as the "music" cue for the test hook. */
export const MUSIC_CLIPS: Record<string, string> = { 'posttime.mp3': 'posttime', 'catmetal.mp3': 'catmetal', 'trip.mp3': 'trip' };
